/**
 * What Lighthouse says on a call, read from the same SessionState the chat renders (never from anything
 * else): the assistant text of the runs the call tracks, as it streams, cut into sentences.
 *
 * - Tracked runs: everything that starts after the call began ("everything Lighthouse says during the call
 *   is spoken"); runs that were already there stay silent.
 * - Preambles: a tool round's text streams under its own message id and never completes; a newer message of
 *   the same run flushes it.
 * - No double speaking: when `message.completed` extends what streamed, only the tail is spoken; when it
 *   rewrites it, nothing more is.
 * - Questions are spoken with their choices; a run that fails with nothing said gets one friendly line;
 *   an approval nobody mentioned gets "I need your OK on that".
 * - A run says at most ~700 characters, then "The rest is in the chat."
 * - With a talker on the call (`setTalker`), its lines (`voice.say`) are spoken only while they're still timely:
 *   an acknowledgement or a progress line never after the run's own words have started (nor once it's over),
 *   a failure line in place of the canned one, an answer about the work as it comes. Its lines that are also
 *   chat messages (`spoken`) aren't spoken a second time from the transcript.
 */
import { friendlyRunError } from '../agent/progress'
import { pendingApprovals, type SessionState, type UiMessage } from '../agent/reducer'
import { IN_THE_CHAT, SentenceChunker, toSpeakable } from './speakable'

export interface AgentSpeechEvents {
    /** `source`: the agent's own words, or a line of the call's voice (the talker) said while it works. */
    speak(text: string, runId: string | null, source?: 'agent' | 'talker'): void
    /** A run is queued / running in the session (true), or none is (false). Fired on change. */
    runActivity(active: boolean): void
    /** A change of a tracked run waits for approval. Fired on change. */
    approval(pending: boolean): void
}

export const SPOKEN = {
    restInChat: 'The rest is in the chat.',
    approval: "I need your OK on that. It's on your screen.",
    form: "I've put a couple of questions on your screen.",
    browser: "The browser needs you for a moment. It's on your screen.",
}

const RUNNING = new Set(['queued', 'running'])

export class AgentSpeech {
    private known = new Set<string>()
    private chunkers = new Map<string, SentenceChunker>()
    private fed = new Map<string, string>()
    private finished = new Set<string>()
    private lastMessageOfRun = new Map<string, string>()
    private spoken = new Map<string, number>()
    private capped = new Set<string>()
    private silenced = new Set<string>()
    private done = new Set<string>()
    private questions = new Set<string>()
    private pointer = new Set<string>()
    private history = new Map<string, string[]>()
    private lastRun: string | null = null
    private active = false
    private approvalPending = false
    private browserKey = ''
    private talker = false
    /** `voice.say` lines already handled (spoken or dropped as stale). */
    private says = new Set<string>()

    constructor(private ev: AgentSpeechEvents, private cap = 700) {}

    /** The call starts: what's already in the session stays silent. */
    begin(st: SessionState): void {
        for (const id of Object.keys(st.runs)) this.known.add(id)
        for (const m of Object.values(st.messages)) if (m.runId) this.known.add(m.runId)
        for (const q of Object.values(st.questions)) this.questions.add(q.id)
        for (const v of st.voiceSays ?? []) this.says.add(v.id)
        this.browserKey = this.browserNeed(st)
    }

    /** The call has a server talker: speak its `voice.say` lines (calls without one never get any). */
    setTalker(on: boolean): void {
        this.talker = on
    }

    /**
     * A run the call itself sent. `live`: it isn't known to be over yet, so the call now counts a run as
     * active; the next update that finds none running says so (even when its start and end arrived together).
     */
    track(runId: string, live = true): void {
        this.known.delete(runId)
        if (live) this.active = true
    }

    private tracked(runId: string | null | undefined): runId is string {
        return Boolean(runId) && !this.known.has(runId!)
    }

    /** Tap to interrupt: the runs still talking say nothing more (it all stays in the chat). */
    silenceCurrent(): void {
        for (const runId of this.lastMessageOfRun.keys()) if (!this.done.has(runId)) this.silenced.add(runId)
        if (this.lastRun) this.silenced.add(this.lastRun)
        for (const [id, c] of this.chunkers) if (c.pending) this.chunkers.set(id, new SentenceChunker())
    }

    /** "Say that again": the last run's spoken sentences. */
    replay(): string[] {
        return this.lastRun ? [...(this.history.get(this.lastRun) ?? [])] : []
    }

    update(st: SessionState): void {
        // The talker's lines first: a failure line it said stands in for the canned one below.
        if (this.talker) this.voiceSays(st)
        for (const item of st.timeline) {
            if (item.kind !== 'message') continue
            const m = st.messages[item.id]
            if (m && m.role === 'assistant' && !m.spoken && this.tracked(m.runId)) this.feed(m)
        }

        const q = st.question
        if (q && !q.answered && this.tracked(q.runId) && !this.questions.has(q.id)) {
            this.questions.add(q.id)
            this.flushRun(q.runId)
            let line = q.question
            if (q.fields.length) line = `${line} ${SPOKEN.form}`
            else if (q.options.length && q.options.length <= 4) line = `${line} You can say ${listOf(q.options)}.`
            this.say(q.runId, toSpeakable(line))
        }

        for (const run of Object.values(st.runs)) {
            if (!this.tracked(run.id) || RUNNING.has(run.status) || this.done.has(run.id)) continue
            this.done.add(run.id)
            this.flushRun(run.id)
            const said = (this.spoken.get(run.id) ?? 0) > 0
            const failure = friendlyRunError(run.status)
            if (failure && !said) this.say(run.id, failure)
            else if (!said && pendingApprovals(st).some((c) => c.runId === run.id)) this.say(run.id, SPOKEN.approval)
        }

        const need = this.browserNeed(st)
        if (need && need !== this.browserKey) this.say(this.lastRun, SPOKEN.browser)
        this.browserKey = need

        const active = Object.values(st.runs).some((r) => RUNNING.has(r.status) && (this.tracked(r.id) || r.id === st.activeRunId))
        if (active !== this.active) {
            this.active = active
            this.ev.runActivity(active)
        }
        const pending = pendingApprovals(st).some((c) => this.tracked(c.runId))
        if (pending !== this.approvalPending) {
            this.approvalPending = pending
            this.ev.approval(pending)
        }
    }

    private voiceSays(st: SessionState): void {
        for (const v of st.voiceSays ?? []) {
            if (this.says.has(v.id)) continue
            this.says.add(v.id)
            if (!this.tracked(v.runId)) continue
            const run = v.runId ? st.runs[v.runId] : undefined
            const running = !run || RUNNING.has(run.status)
            if (v.kind === 'ack' || v.kind === 'progress') {
                // Filler: only while the run still works and before its own words. A "Got it" for words said
                // mid-task answers those, so the run having spoken earlier doesn't make it stale.
                const steerAck = v.kind === 'ack' && Boolean(v.replyTo) && v.replyTo !== v.runId
                const started = this.lastMessageOfRun.has(v.runId!) && !steerAck
                if (!running || started || this.silenced.has(v.runId!)) continue
                this.ev.speak(toSpeakable(v.text), v.runId, 'talker')
            } else {
                // A failure or an answer about the work: said like the run's own words (counted as said).
                this.say(v.runId, toSpeakable(v.text))
            }
        }
    }

    private browserNeed(st: SessionState): string {
        const b = st.browser
        if (b.status !== 'active') return ''
        if (b.controller === 'user' || b.taskStatus === 'waiting_creator') return `${b.taskId ?? ''}:${b.handoffReason ?? ''}`
        return ''
    }

    private chunker(id: string): SentenceChunker {
        let c = this.chunkers.get(id)
        if (!c) {
            c = new SentenceChunker()
            this.chunkers.set(id, c)
        }
        return c
    }

    private feed(m: UiMessage): void {
        if (this.finished.has(m.id)) return
        const runId = m.runId!
        const before = this.lastMessageOfRun.get(runId)
        if (before && before !== m.id) this.finish(before, runId)
        this.lastMessageOfRun.set(runId, m.id)
        const prev = this.fed.get(m.id) ?? ''
        const c = this.chunker(m.id)
        if (!m.streaming) {
            const chunks = !prev
                ? [...c.push(m.text), ...c.flush()]
                : m.text.startsWith(prev)
                    ? [...c.push(m.text.slice(prev.length)), ...c.flush()]
                    : c.flush() // rewritten after streaming: what streamed is the version spoken
            this.fed.set(m.id, m.text)
            this.finished.add(m.id)
            this.speakChunks(runId, chunks)
            return
        }
        if (!m.text.startsWith(prev)) return
        this.fed.set(m.id, m.text)
        this.speakChunks(runId, c.push(m.text.slice(prev.length)))
    }

    private finish(messageId: string, runId: string): void {
        if (this.finished.has(messageId)) return
        this.finished.add(messageId)
        this.speakChunks(runId, this.chunker(messageId).flush())
    }

    private flushRun(runId: string | null): void {
        if (!runId) return
        const id = this.lastMessageOfRun.get(runId)
        if (id) this.finish(id, runId)
    }

    private speakChunks(runId: string, chunks: string[]): void {
        for (const raw of chunks) this.say(runId, toSpeakable(raw))
    }

    private say(runId: string | null, raw: string): void {
        const key = runId ?? ''
        if (runId && (this.silenced.has(runId) || this.capped.has(runId))) return
        let text = raw
        if (text.includes(IN_THE_CHAT)) {
            // "I've put it in the chat" once per run, however many code blocks or tables it wrote.
            const parts = text.split(IN_THE_CHAT)
            const keep = !this.pointer.has(key)
            text = `${parts[0]}${keep ? IN_THE_CHAT : ''} ${parts.slice(1).join(' ')}`.replace(/\s{2,}/g, ' ').trim()
            if (keep) this.pointer.add(key)
        }
        if (!text) return
        const soFar = this.spoken.get(key) ?? 0
        if (runId && soFar > 0 && soFar + text.length > this.cap) {
            this.capped.add(runId)
            this.emit(runId, SPOKEN.restInChat)
            return
        }
        this.spoken.set(key, soFar + text.length)
        this.emit(runId, text)
    }

    private emit(runId: string | null, text: string): void {
        if (runId) {
            if (this.lastRun !== runId) this.history.set(runId, [])
            this.lastRun = runId
            this.history.get(runId)!.push(text)
        }
        this.ev.speak(text, runId, 'agent')
    }
}

function listOf(options: string[]): string {
    if (options.length <= 1) return options[0] ?? ''
    return `${options.slice(0, -1).join(', ')} or ${options[options.length - 1]}`
}
